/**
 * MODULE: _shared/frontend-shell/src/features/crm/data/opportunityDealFrameworkSections.ts
 * PURPOSE: MEDDPICC, 3 Whys, and Value Framework field specs for OpportunityDrawer (labels + CRM field keys)
 * DEPENDENCIES: ../types/opportunity.types (OpportunityFieldKey)
 * PUBLIC_API: OPPORTUNITY_DEAL_FRAMEWORK_SECTIONS
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~64 / 150
 * RECENT: 2026-05-01 [2.2.778] — MEDDPICC, 3 Whys, Value Framework section specs for **`OpportunityDrawer`**; root **`CHANGELOG` [2.2.778]`**
 */

import type { OpportunityFieldKey } from '../types/opportunity.types';

export type DealFrameworkFieldSpec = { readonly key: OpportunityFieldKey; readonly label: string };

export type DealFrameworkSectionSpec = {
  readonly title: string;
  readonly testId: string;
  readonly fields: readonly DealFrameworkFieldSpec[];
};

/** Collapsible deal modal sections — one paragraph (textarea) per field. */
export const OPPORTUNITY_DEAL_FRAMEWORK_SECTIONS: readonly DealFrameworkSectionSpec[] = [
  {
    title: 'MEDDPICC',
    testId: 'opportunity-section-meddpicc',
    fields: [
      { key: 'meddpiccMetrics', label: 'Metrics' },
      { key: 'meddpiccEconomicBuyer', label: 'EB' },
      { key: 'meddpiccDecisionProcess', label: 'Decision process' },
      { key: 'meddpiccDecisionCriteria', label: 'Decision criteria' },
      { key: 'meddpiccPaperProcess', label: 'Paper process' },
      { key: 'meddpiccIdentifiedPain', label: 'Identified pain' },
      { key: 'meddpiccChampion', label: 'Champion' },
      { key: 'meddpiccCompetition', label: 'Competition' },
    ],
  },
  {
    title: '3 Whys',
    testId: 'opportunity-section-three-whys',
    fields: [
      { key: 'threeWhysAnything', label: 'Why anything' },
      { key: 'threeWhysYourCompany', label: 'Why your company' },
      { key: 'threeWhysNow', label: 'Why now' },
    ],
  },
  {
    title: 'Value Framework',
    testId: 'opportunity-section-value-framework',
    fields: [
      { key: 'valueFrameworkCurrentSituation', label: 'Current situation' },
      { key: 'valueFrameworkNegativeConsequences', label: 'Negative consequences' },
      { key: 'valueFrameworkPositiveOutcomes', label: 'Positive business outcomes' },
      { key: 'valueFrameworkRequiredCapabilities', label: 'Required capabilities' },
      { key: 'valueFrameworkHowWeDoIt', label: 'How we do it' },
      { key: 'valueFrameworkHowWeDoItBetter', label: 'How we do it better' },
      { key: 'valueFrameworkProofPoints', label: 'Proof points' },
    ],
  },
] as const;

/** Keys included in deal PATCH when any framework textarea is saved. */
export const OPPORTUNITY_DEAL_FRAMEWORK_PATCH_KEYS: OpportunityFieldKey[] =
  OPPORTUNITY_DEAL_FRAMEWORK_SECTIONS.flatMap((s) => s.fields.map((f) => f.key)) as OpportunityFieldKey[];
